import { CheckIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import type { EmptyVariant } from '@/lib/tryout/tryoutRun';

interface TryoutVariantPickerProps {
  variants: Array<{ variant: EmptyVariant; url: string }>;
  selected: EmptyVariant | null;
  onSelect: (v: EmptyVariant) => void;
  aspectRatio: '1:1' | '4:3';
  /** When set, an "Original" reference card leads the row so the before is
   *  always visible next to the emptied options. */
  beforeUrl?: string;
}

// Makler-friendly labels — never expose "Variant A/B" as jargon.
const LABEL: Record<EmptyVariant, string> = { A: 'Vorschlag 1', B: 'Vorschlag 2' };

const CornerLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent pointer-events-none" />
    <div className="absolute inset-x-0 bottom-0 px-3 py-2 text-left">
      <span className="text-[13px] font-semibold text-white drop-shadow-sm">{children}</span>
    </div>
  </>
);

const Watermark = () => (
  <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden">
    <span className="rotate-[-30deg] text-white/25 text-base font-bold tracking-widest whitespace-nowrap">estage.ai</span>
  </div>
);

export const TryoutVariantPicker: React.FC<TryoutVariantPickerProps> = ({
  variants, selected, onSelect, aspectRatio, beforeUrl,
}) => {
  const ar = aspectRatio === '1:1' ? '1 / 1' : '4 / 3';
  const cols = beforeUrl ? 'sm:grid-cols-3' : 'sm:grid-cols-2';

  return (
    <div className={cn('grid grid-cols-1 gap-3', cols)} style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}>
      {/* Original reference — not selectable, keeps the "before" in view */}
      {beforeUrl && (
        <div className="relative block w-full overflow-hidden rounded-[14px] ring-1 ring-[#EBEBE6]" style={{ aspectRatio: ar }}>
          <img src={beforeUrl} alt="Original" draggable={false}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          <CornerLabel>Original</CornerLabel>
        </div>
      )}

      {variants.map(({ variant, url }) => {
        const active = selected === variant;
        return (
          <button
            key={variant}
            onClick={() => onSelect(variant)}
            className={cn(
              'group relative block w-full overflow-hidden rounded-[14px] transition-all duration-150',
              active ? 'ring-[3px] ring-[#FE6F17]' : 'ring-1 ring-[#EBEBE6] hover:ring-[#FE6F17]/50',
            )}
            style={{ aspectRatio: ar }}
            aria-pressed={active}
          >
            <img src={url} alt={LABEL[variant]} draggable={false}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            <Watermark />
            <CornerLabel>{LABEL[variant]}</CornerLabel>
            {active && (
              <div className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-[#FE6F17] border-2 border-white flex items-center justify-center shadow-sm">
                <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};
